
import React from 'react';
import Layout from '@/components/Layout/Layout';
import InterestCalculator from '@/components/SpecialCalculators/FinancialCalculators/InterestCalculator';
import { Link } from 'react-router-dom';

const InterestCalculatorPage = () => {
  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:underline">Home</Link> {' > '} 
            <Link to="/special" className="hover:underline">Special Tools</Link> {' > '} 
            <span>Interest Calculator</span>
          </div>
          <h1 className="text-3xl font-bold mb-3">Interest Calculator</h1>
          <p className="text-gray-700">
            Calculate simple and compound interest, understand how your money grows over time, and compare different interest scenarios.
          </p>
        </div>

        {/* Calculator Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <InterestCalculator />
        </div>

        {/* Information Section */}
        <div className="bg-[#E6F3FF] p-6 rounded-lg mb-8">
          <h2 className="text-2xl font-semibold mb-3">How to use the Interest Calculator</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Choose between simple or compound interest calculation</li>
            <li>Enter the principal amount (initial investment or loan)</li>
            <li>Input the interest rate as a percentage</li>
            <li>Specify the time period</li>
            <li>For compound interest, select the compounding frequency</li>
            <li>The calculator will show the interest earned and total amount</li>
          </ol>
        </div>

        {/* Additional Information */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Simple vs. Compound Interest</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="text-xl font-medium">Simple Interest</h3>
              <p className="text-gray-700">
                Simple interest is calculated only on the initial principal. The formula is:
              </p>
              <div className="bg-gray-100 p-3 rounded-md">
                <p className="font-medium">Interest = Principal × Rate × Time</p>
              </div>
              <p className="text-gray-700">
                Simple interest is common for short-term loans, bonds, and some deposit accounts. Since interest is not 
                compounded, the interest amount remains the same for each period.
              </p>
            </div>
            
            <div className="space-y-3">
              <h3 className="text-xl font-medium">Compound Interest</h3>
              <p className="text-gray-700">
                Compound interest is calculated on both the initial principal and the accumulated interest. The formula is:
              </p>
              <div className="bg-gray-100 p-3 rounded-md">
                <p className="font-medium">A = P(1 + r/n)^(nt)</p>
                <p className="text-sm text-gray-600">
                  Where:<br />
                  A = Final amount<br />
                  P = Principal<br />
                  r = Annual interest rate (decimal)<br />
                  n = Compounding frequency per year<br />
                  t = Time in years
                </p>
              </div>
              <p className="text-gray-700">
                Compound interest leads to exponential growth over time and is commonly used for investments, savings accounts, 
                and long-term loans.
              </p>
            </div>
          </div>
        </div>

        {/* Related Calculators */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Related Calculators</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/special/investment" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">📈</span> Investment Calculator
            </Link>
            <Link to="/special/loan" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">💰</span> Loan Calculator
            </Link>
            <Link to="/special/mortgage" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🏠</span> Mortgage Calculator
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InterestCalculatorPage;
